import debounce from "lodash.debounce";

export const EVENT_DISPLAYED = "photoGalleryDisplayed";
export const EVENT_BROWSE = "photoGalleryBrowse";

export const EVENT_REASON_CLICK_BTN_PREV = "btn-previous";
export const EVENT_REASON_CLICK_BTN_NEXT = "btn-next";
export const EVENT_REASON_CLICK_BTN_DOT = "btn-dot";

export default class PhotoGallery
{
    /**
     * @type {Element}
     * @private
     */
    _rootNode;

    /**
     * @type {number}
     * @private
     */
    _baseWidth = 0;

    /**
     * @type {Element}
     * @private
     */
    _rollNode = null;

    /**
     * @type {Array}
     * @private
     */
    _items = [];

    /**
     * @type {number}
     * @private
     */
    _currentIndex = 0;

    /**
     * @type {Element}
     * @private
     */
    _navigationContextNode;

    _metaData = {};

    constructor(node, metaData) {
        this._setupLayout = this._setupLayout.bind(this);

        this._metaData = metaData;
        this._rootNode = node;
        this._navigationContextNode = this._rootNode.querySelector(".photoGalleryModule__navigation");
        this._rollNode = this._rootNode.querySelector(".photoGalleryModule__roll");

        this._setupEventListener();
        this._setupEventGenerator();
        this._setupItems();
    }

    slide(inc, reason) {
        const animatedCssClass = "photoGalleryModule__roll--animated";
        const setNewIndex = (index) => {
            this._currentIndex = index;
            this._updateNavigationIndicators();
            if (reason) {
                this._dispatchEvent(EVENT_BROWSE, this._currentIndex, reason);
            }
        };

        if (this._currentIndex === 0 && inc < 0) {
            this._rollNode.classList.remove(animatedCssClass);
            this._rollNode.style.left = (this._items.length * this._baseWidth * -1) + "px";
            this._currentIndex = this._items.length;
        }

        const newIndex = this._currentIndex + inc;

        new Promise((resolve) => {
            setTimeout(() => {
                this._rollNode.classList.add(animatedCssClass);
                this._rollNode.style.left = (newIndex * this._baseWidth * -1) + "px";
                resolve();
            }, 0);
        }).then(() => {
            if (newIndex >= this._items.length) {
                setTimeout(() => {
                    this._rollNode.classList.remove(animatedCssClass);
                    this._rollNode.style.left = 0;
                }, 300);
                setNewIndex(0);
            } else {
                this._currentIndex = newIndex;
                setNewIndex(newIndex);
            }
        });
    }

    _updateNavigationIndicators() {
        this._rootNode.querySelector(".photoGalleryModule__navCounterCurrent").innerHTML = (this._currentIndex + 1);
        this._navigationContextNode.querySelector(".photoGalleryModule__navigationDot--active").classList.remove("photoGalleryModule__navigationDot--active");
        this._navigationContextNode.querySelector(".photoGalleryModule__navigationDot:nth-child(" + (this._currentIndex + 1) + ")").classList.add("photoGalleryModule__navigationDot--active");
        if (this._navigationContextNode.querySelector(".photoGalleryModule__navigationDot--active-dynamic-style")) {
            this._navigationContextNode.querySelector(".photoGalleryModule__navigationDot--active-dynamic-style").classList.remove("photoGalleryModule__navigationDot--active-dynamic-style");
            this._navigationContextNode.querySelector(".photoGalleryModule__navigationDot:nth-child(" + (this._currentIndex + 1) + ")").classList.add("photoGalleryModule__navigationDot--active-dynamic-style");
        }
    }
    _setupEventGenerator() {
        let observer;
        let shouldTriggerEvent = true;

        const options = {
            root: null,
            rootMargin: "0px",
            threshold: [0.1, 0.7]
        };

        observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.intersectionRatio >= 0.7 && shouldTriggerEvent) {
                    shouldTriggerEvent = false;
                    this._dispatchEvent(EVENT_DISPLAYED, this._currentIndex);
                } else if (entry.intersectionRatio < 0.1) {
                    shouldTriggerEvent = true;
                }
            });
        }, options);
        observer.observe(this._rootNode);
    }

    _dispatchEvent(eventName, itemIdx, reason) {
        let evt = new CustomEvent(eventName, { bubbles: true });

        evt.data = Object.assign({
            itemIdx: itemIdx + 1,
            nbTotalItem: this._items.length,
            reason
        }, this._metaData);

        // const win = this._rootNode.ownerDocument.defaultView || this._rootNode.parentWindow;
        // @todo - enable metrics event when GTM and snowplow will be ready
        // win && win.dispatchEvent(evt)
    }

    _setupEventListener() {
        const navigationBtnsNodes = Array.from(this._navigationContextNode.querySelectorAll(".photoGalleryModule__navigationDot"));
        this._navigationContextNode.addEventListener("click", (e) => {
            const idx = navigationBtnsNodes.indexOf(e.target);
            if (idx !== -1) {
                this.slide(idx - this._currentIndex, EVENT_REASON_CLICK_BTN_DOT);
            }
        });

        const nextBtn = this._rootNode.querySelector(".photoGalleryModule__navBtn--next");
        nextBtn.addEventListener("click", this.slide.bind(this, 1, EVENT_REASON_CLICK_BTN_NEXT));

        const prevBtn = this._rootNode.querySelector(".photoGalleryModule__navBtn--previous");
        prevBtn.addEventListener("click", this.slide.bind(this, -1, EVENT_REASON_CLICK_BTN_PREV));

        const win = this._rootNode.ownerDocument.defaultView || this._rootNode.parentWindow;
        win.addEventListener("resize", debounce(this._setupLayout, 100));

        this._rootNode.querySelector(".photoGalleryModule__item img").addEventListener("load", this._setupLayout);
    }

    _setupItems() {
        this._items = Array.from(this._rollNode.querySelectorAll(".photoGalleryModule__item"));

        this._setupLayout();

        let node = this._items[0].cloneNode(true);
        node.classList.add("photoGalleryModule__item--clone");

        this._rollNode.append(node);
    }

    _setupLayout() {
        this._updateHeight();
        this._rollNode.style.width = ((this._items.length + 1) * this._baseWidth) + "px";
        this.slide(0);
    }

    _updateHeight() {
        const wrapper = this._rootNode.querySelector(".photoGalleryModule__wrapper");
        const galleryModuleItems = wrapper.querySelectorAll(".photoGalleryModule__item");
        this._baseWidth = wrapper.clientWidth;

        wrapper.querySelectorAll(".photoGalleryModule__visual").forEach(img => {
            img.style.minHeight = null;
        });

        const bottomBorderHeight = 2;
        let img = null;
        let caption = null;
        let maxImgHeight = 0;
        let maxCaptionHeight = 0;
        // setting all items width to wrapper width and getting max image height and max caption height
        galleryModuleItems.forEach(nodeItem => {
            nodeItem.style.width = this._baseWidth + "px";
            img = nodeItem.querySelector(".photoGalleryModule__visual");
            caption = nodeItem.querySelector("figcaption");

            if (img.clientHeight > maxImgHeight) {
                maxImgHeight = img.clientHeight;
            }
            if (caption.offsetHeight > maxCaptionHeight) {
                maxCaptionHeight = caption.offsetHeight;
            }
        });
        // setting wrapper height from max image height and max caption height + bottom border
        wrapper.style.height = (maxImgHeight + bottomBorderHeight + maxCaptionHeight) + "px";
        if (maxCaptionHeight === 0) {
            this._rootNode.classList.add("module--no-caption");
        } else {
            this._rootNode.classList.remove("module--no-caption");
        }
        // centering navigation buttons on image
        wrapper.querySelectorAll(".photoGalleryModule__navBtn").forEach(button => {
            button.style.top = ((maxImgHeight / 2) - (button.offsetHeight / 2)) + "px";
        });

        // setting min height of all images to max image height
        wrapper.querySelectorAll(".photoGalleryModule__visual").forEach(img => {
            img.style.minHeight = maxImgHeight + "px";
        });

    }

}
